import Vue from "vue";
import Router from "vue-router";
import multiguard from "vue-router-multiguard";
import i18n from "./i18n";
import store from "./store";

Vue.use(Router);

function requireUser(to, from, next) {
  store.dispatch("fetchUser").then(() => {
    // try to get current user, and redirect to signin if we can't
    let user = store.getters.currentUser;
    if (user) {
      let redirectUrl = store.getters.forceRedirect;
      if (redirectUrl) {
        let resolved = router.resolve(redirectUrl);
        if (resolved.route.name !== to.name) {
          // wants to redirect us elsewhere
          if (resolved.route.name) {
            // destination is in vue
            resolved.location.query.destination = to.fullPath;
            next(resolved.location);
            return;
          } else {
            // destination is not in vue
            let redirect =
              redirectUrl + "?destination=" + encodeURIComponent(to.fullPath);
            location.replace(redirect);
            return;
          }
        }
      }
      next();
    }
  });
}

async function requireNoUser(to, from, next) {
  // if we are already signed in, redirect to programs page
  try {
    await store.dispatch("fetchUser", { noRedirect: true });
    if (sessionStorage.redir) {
      location.replace(sessionStorage.redir);
    } else {
      location.replace("/programs");
    }
  } catch (e) {
    next();
  }
}

function requireEdition(to, from, next) {
  store.dispatch("fetchEdition").then(() => {
    // ensure that edition is loaded
    let edition = store.getters.currentEdition;
    if (edition) {
      next();
    }
  });
}

function redirectIfInactive(to, from, next) {
  let user = store.getters.currentUser;
  if (user.inactive_subscription) {
    // Only show top level views with lists of content to users with inactive subscriptions
    let listView = to.path.match(/^\/[^\/]+/)[0];
    store.commit("setTriggerInactiveSubscriptionModal", true);
    next(listView);
  } else {
    next();
  }
}

const router = new Router({
  mode: "history",
  base: "/",
  scrollBehavior(to, from, savedPosition) {
    const position = { x: 0, y: 0 };
    if (savedPosition) {
      position.y = savedPosition.y;
    } else if (
      (to.name === "program" && from.name === "programs") ||
      (to.name === "programs" && from.name == "program")
    ) {
      position.y = window.scrollY;
    }
    return position;
  },
  routes: [
    {
      path: '/',
      beforeEnter: multiguard([
        requireUser,
        requireEdition,
        (to, from, next) => {
          if (
            sessionStorage.getItem('homeJourney') ||
            store.getters.currentUser.home_journey ||
            store.getters.currentEdition.enable_home_page
          ) {
            next('/home');
          } else {
            next('/programs');
          }
        },
      ]),
    },
    {
      path: "/learn/*",
      redirect: (to) => to.path.replace(/^\/learn\//, "/"),
    },
    {
      name: 'home',
      path: '/home',
      component: () => {
        if (
          (store.getters.currentUser.home_journey ||
            sessionStorage.getItem('homeJourney')) &&
          !store.getters.currentEdition.enable_home_page
        ) {
          return import('./home/HomePage.vue');
        } else {
          return import('./home/HomePagePreview.vue');
        }
      },
      // route level code-splitting
      // this generates a separate chunk (programs.[hash].js) for this route
      // which is lazy-loaded when the route is visited.
      meta: { title: "home", template: () => import("@/layouts/standard") },
      beforeEnter: multiguard([requireEdition, requireUser,
        (to, from, next) => {
          if (
            sessionStorage.getItem('homeJourney') ||
            store.getters.currentUser.home_journey ||
            store.getters.currentEdition.enable_home_page
          ) {
            next();
          } else {
            next('/programs');
          }
        },
      ]),
    },
    {
      path: "/home-preview",
      component: () => import("./home/HomePagePreview.vue"),
      // route level code-splitting
      // this generates a separate chunk (programs.[hash].js) for this route
      // which is lazy-loaded when the route is visited.
      meta: { title: "home", template: () => import("@/layouts/standard") },
      beforeEnter: multiguard([requireEdition, requireUser]),
    },
    {
      path: "/programs",
      component: () => import("./programs/ProgramsContainer.vue"),
      meta: { title: "programs", template: () => import("@/layouts/standard") },
      beforeEnter: multiguard([requireEdition, requireUser]),
      children: [
        {
          path: "",
          name: "programs",
          props: (route) => ({
            programIdParam: Number(route.params.programIdParam),
          }),
          component: () => import("./programs/Programs.vue"),
          meta: { template: () => import("@/layouts/standard") },
        },
        {
          path: "scheduled",
          component: () => import("./programs/UpcomingPrograms"),
          meta: { template: () => import("@/layouts/standard") },
        },
        {
          path: "registered",
          component: () => import("./programs/ProgramsByTag"),
          props: (route) => {
            return {
              tag: { name: i18n.t("message.your-upcoming-programs") },
              programs: store.getters.registeredPrograms,
            };
          },
          meta: { template: () => import("@/layouts/standard") },
        },
        {
          path: "active",
          component: () => import("./programs/ProgramsByTag"),
          props: (route) => {
            return {
              tag: { name: i18n.t("message.your-active-programs") },
              programs: store.getters.activePrograms,
              current: true,
            };
          },
          meta: { template: () => import("@/layouts/standard") },
        },
        {
          path: "previous",
          component: () => import("./programs/ProgramsByTag"),
          props: (route) => {
            return {
              tag: { name: i18n.t("message.your-previous-programs") },
              programs: store.getters.previousPrograms,
              plain: true,
            };
          },
          meta: { template: () => import("@/layouts/standard") },
        },
        {
          path: "always-on",
          component: () => import("./programs/ProgramsByTag"),
          props: (route) => {
            return {
              tag: { name: store.getters.currentEdition.always_on_label },
              programs: store.getters.alwaysOnPrograms,
              plain: true,
            };
          },
          meta: { template: () => import("@/layouts/standard") },
        },
        {
          path: "coming-soon",
          component: () => import("./programs/ProgramsByTag"),
          props: (route) => {
            return {
              tag: { name: i18n.t("message.coming-soon") },
              programs: store.getters.comingSoonPrograms,
              plain: true,
            };
          },
          meta: { template: () => import("@/layouts/standard") },
        },
        {
          path: "tags/:tagSlug",
          component: () => import("./programs/ProgramsByTag"),
          props: (route) => {
            const tag = store.getters.programTagBySlug(route.params.tagSlug);
            return {
              tag,
              programs: store.getters.programsByTag(tag),
            };
          },
          meta: { template: () => import("@/layouts/standard") },
        },
        {
          path: "skills/:tagSlug",
          component: () => import("./programs/ProgramsBySkill"),
          props: (route) => {
            const tag = store.getters.programTagBySlug(route.params.tagSlug);
            return {
              tag,
              programs: store.getters.programsByTag(tag),
            };
          },
          meta: { template: () => import("@/layouts/standard") },
        },
        {
          path: "types/:typeSlug",
          component: () => import("./programs/ProgramsByType"),
          props: (route) => ({
            type: store.getters.programTypeBySlug(route.params.typeSlug),
          }),
          meta: { template: () => import("@/layouts/standard") },
        },
        {
          path: "admin",
          component: () => import("./programs/ProgramsByTag"),
          props: (route) => {
            return {
              tag: { name: i18n.t("message.programs-you-administer") },
              programs: store.getters.adminOrOwnerPrograms,
              asAdmin: true,
              plain: true,
            };
          },
          meta: { template: () => import("@/layouts/standard") },
        },
        {
          path: ":programIdParam",
          name: "program",
          props: (route) => ({
            programIdParam: Number(route.params.programIdParam),
          }),
          component: () => import("./programs/Programs.vue"),
          meta: { template: () => import("@/layouts/standard") },
        },
      ],
    },
    {
      path: "/signin",
      name: "signin",
      component: () => import("./signup/Signin.vue"),
      beforeEnter: multiguard([requireEdition, requireNoUser]),
      meta: {
        title: "sign-in",
        template: () => import("@/signup/SharedTemplate"),
      },
    },
    {
      path: "/sso",
      name: "sso",
      component: () => import("./signup/SSO.vue"),
      beforeEnter: multiguard([requireEdition, requireNoUser]),
      meta: {
        title: "sign-in",
        template: () => import("@/signup/SharedTemplate"),
        templateProps: {
          hideSso: true,
        },
      },
    },
    {
      path: "/sso/error",
      name: "sso-error",
      component: () => import("./signup/SSOError.vue"),
      beforeEnter: multiguard([requireEdition, requireNoUser]),
      meta: { title: "sign-in", template: () => import("@/layouts/signin") },
    },
    {
      path: "/register",
      name: "register",
      component: () => import("./signup/Register.vue"),
      beforeEnter: multiguard([requireEdition, requireNoUser]),
      meta: {
        title: "create-account",
        template: () => import("@/signup/SharedTemplate"),
      },
    },
    {
      path: "/forgot-password",
      name: "forgot-password",
      component: () => import("./signup/ForgotPassword.vue"),
      beforeEnter: multiguard([requireEdition, requireNoUser]),
      meta: {
        title: "forgot-password-title",
        template: () => import("@/signup/SharedTemplate"),
        templateProps: {
          hideSso: true,
        },
      },
    },
    {
      path: "/resetpw",
      redirect: { name: "reset-password" },
    },
    {
      path: "/reset-password",
      name: "reset-password",
      component: () => import("./signup/ResetPassword.vue"),
      beforeEnter: multiguard([requireEdition, requireNoUser]),
      meta: {
        title: "reset-password-title",
        template: () => import("@/signup/SharedTemplate"),
        templateProps: {
          hideSso: true,
        },
      },
    },
    {
      path: "/profile/update",
      name: "complete-profile",
      component: () => import("./signup/CompleteProfile.vue"),
      beforeEnter: multiguard([requireEdition, requireUser]),
      meta: {
        title: "complete-profile-title",
        template: () => import("@/layouts/signin"),
        templateProps: {
          showClose: true,
        },
      },
    },
    {
      path: "/promo/:token",
      beforeEnter: (to, from, next) => {
        if (to.params.token) {
          sessionStorage.discount = to.params.token;
        }
        next({ name: "register" });
      },
    },
    {
      path: "/community",
      name: "community",
      component: () => import("./community/CommunityWrapper.vue"),
      beforeEnter: multiguard([requireEdition, requireUser]),
      meta: {
        title: "community",
        template: () => import("@/layouts/standard"),
      },
    },
    {
      path: "/community/private-communities/:channelIdParam",
      name: "private-community",
      component: () => import("./community/channels/Channel.vue"),
      props: (route) => ({
        channelIdParam: Number(route.params.channelIdParam),
        privateCommunity: true,
      }),
      beforeEnter: multiguard([requireEdition, requireUser]),
      meta: {
        title: "community",
        template: () => import("@/layouts/standard"),
      },
    },
    {
      path: "/community/private-communities/:channelIdParam/post?/:postIdParam",
      name: "private-community",
      component: () => import("./community/channels/Channel.vue"),
      props: (route) => ({
        postIdParam: Number(route.params.postIdParam),
        channelIdParam: Number(route.params.channelIdParam),
        privateCommunity: true,
      }),
      beforeEnter: multiguard([requireEdition, requireUser]),
      meta: {
        title: "community",
        template: () => import("@/layouts/standard"),
      },
    },
    {
      path: "/community/post/:postIdParam",
      name: "community-post",
      props: (route) => ({ postIdParam: Number(route.params.postIdParam) }),
      component: () => import("./community/Community.vue"),
      meta: {
        title: "community",
        template: () => import("@/layouts/standard"),
      },
      beforeEnter: multiguard([
        requireEdition,
        requireUser,
        redirectIfInactive,
      ]),
    },
    {
      path: "/community/channels/:channelIdParam",
      name: "channel",
      props: (route) => ({
        channelIdParam: Number(route.params.channelIdParam),
      }),
      component: () => import("./community/channels/Channel.vue"),
      beforeEnter: multiguard([
        requireEdition,
        requireUser,
        redirectIfInactive,
      ]),
      meta: {
        title: "community",
        template: () => import("@/layouts/standard"),
      },
    },
    {
      path: "/community/channels/:channelIdParam/post/:postIdParam",
      name: "channel-post",
      props: (route) => ({
        postIdParam: Number(route.params.postIdParam),
        channelIdParam: Number(route.params.channelIdParam),
      }),
      component: () => import("./community/channels/Channel.vue"),
      beforeEnter: multiguard([
        requireEdition,
        requireUser,
        redirectIfInactive,
      ]),
      meta: {
        title: "community",
        template: () => import("@/layouts/standard"),
      },
    },
    {
      path: "/community/public-channels",
      name: "public-channels",
      component: () => import("./community/channels/PublicChannels.vue"),
      beforeEnter: multiguard([requireEdition, requireUser]),
      meta: {
        title: "public-channels",
        template: () => import("@/layouts/standard"),
      },
    },
    {
      path: "/community/live",
      name: "community-live",
      component: () => import("./community/live/Live.vue"),
      beforeEnter: multiguard([requireEdition, requireUser]),
      meta: { template: () => import("@/layouts/standard") },
    },
    {
      path: "/resource-library",
      name: "resource-library",
      component: () => import("./resources/ResourceLibrary.vue"),
      beforeEnter: multiguard([requireEdition, requireUser]),
      meta: {
        title: "resources",
        template: () => import("@/layouts/standard"),
      },
    },
    {
      path: "/payment",
      name: "payment",
      component: () => import("./signup/PaymentPage.vue"),
      meta: {
        title: "payment",
        template: () => import("@/layouts/signin"),
        templateProps: {
          showClose: true,
        },
      },
      beforeEnter: (to, from, next) => {
        store.dispatch("fetchUserAndEdition").then(() => {
          // only allow access to payment page if paying user who doesn't have a subscription yet
          let user = store.getters.currentUser;
          if (user && user.require_payment && !user.is_in_subscription) {
            next();
          } else {
            next("/programs");
          }
        });
      },
    },
    {
      path: "/settings/billing",
      name: "billing_settings",
      component: () => import("./settings/Billing.vue"),
      meta: {
        title: "billing-and-payment",
        template: () => import("@/layouts/settings"),
      },
      beforeEnter: (to, from, next) => {
        store.dispatch("fetchUserAndEdition").then(() => {
          let user = store.getters.currentUser;
          if (user.has_subscriptions) {
            next();
          } else {
            next("/settings");
          }
        });
      },
    },
    {
      path: "/settings/email",
      name: "email_settings",
      component: () => import("./settings/Email.vue"),
      meta: {
        title: "email-notifications",
        template: () => import("@/layouts/settings"),
      },
      beforeEnter: multiguard([requireEdition, requireUser]),
    },
    {
      path: "/settings/security",
      name: "security_settings",
      component: () => import("./settings/Security.vue"),
      meta: {
        title: "change-password",
        template: () => import("@/layouts/settings"),
      },
      beforeEnter: multiguard([
        requireEdition,
        requireUser,
        (to, from, next) => {
          let user = store.getters.currentUser;
          if (user && !user.uses_sso) {
            next();
          } else {
            next("/settings");
          }
        },
      ]),
    },
    {
      path: "/settings",
      redirect: "/settings/email",
    },
    {
      path: "/welcome",
      name: "welcome",
      component: () => import("./more/Welcome.vue"),
      beforeEnter: multiguard([requireEdition, requireUser]),
      meta: {
        title: "welcome-message",
        template: () => import("@/layouts/standard"),
        templateProps: {
          hideNavigation: true,
        },
      },
    },
    {
      path: "/about",
      name: "about",
      component: () => import("./more/About.vue"),
      beforeEnter: multiguard([requireEdition, requireUser]),
      meta: { title: "about", template: () => import("@/layouts/standard") },
    },
    {
      path: "/termsandconditions",
      name: "terms_and_conditions",
      component: () => import("./more/TermsAndConditions.vue"),
      beforeEnter: multiguard([requireEdition, requireUser]),
      meta: {
        title: "ts-and-cs",
        template: () => import("@/layouts/standard"),
      },
    },
    {
      path: "/privacy",
      name: "privacy",
      component: () => import("./more/Privacy.vue"),
      beforeEnter: multiguard([requireEdition, requireUser]),
      meta: { title: "privacy", template: () => import("@/layouts/standard") },
    },
    {
      path: "/cookies",
      name: "cookies",
      component: () => import("./more/Cookies.vue"),
      beforeEnter: multiguard([requireEdition, requireUser]),
      meta: { title: "cookies", template: () => import("@/layouts/standard") },
    },
    {
      path: "/community/group",
      name: "group",
      component: () => import("./community/channels/Channel.vue"),
      beforeEnter: multiguard([requireEdition, requireUser]),
      meta: {
        title: "community",
        template: () => import("@/layouts/standard"),
      },
    },
    {
      path: "/resource-library/:resourceRef",
      name: "resource_detail",
      component: () => import("./resources/ResourceDetail.vue"),
      props: (route) => ({
        preview: Boolean(route.params.preview),
        resourceRef: String(route.params.resourceRef),
      }),
      beforeEnter: multiguard([
        requireEdition,
        requireUser,
        redirectIfInactive,
      ]),
      meta: {
        title: "resources",
        template: () => import("@/layouts/standard"),
      },
    },
    {
      path: "/resource-library/edit/:resourceRef",
      name: "resource_editor",
      props: (route) => ({
        resourceRef: String(route.params.resourceRef),
      }),
      component: () => import("./resources/ResourceEditor.vue"),
      meta: { template: () => import("@/layouts/admin") },
      beforeEnter: multiguard([
        requireEdition,
        requireUser,
        redirectIfInactive,
        (to, from, next) => {
          let user = store.getters.currentUser;
          if (
            user.admin_level === "sales" ||
            user.permissions.includes("edit_resources")
          ) {
            next();
          } else {
            next({
              name: "resource_detail",
              params: { resourceRef: to.params.resourceRef },
            });
          }
        },
      ]),
    },
    {
      path: "/msteams/tab/authorize",
      name: "msteams_tab_authorize",
      component: () => import("./msteams/TabAuthorize.vue"),
      beforeEnter: requireEdition,
    },
    {
      path: "/msteams/tab/config",
      name: "msteams_tab_config",
      component: () => import("./msteams/TabConfig.vue"),
      beforeEnter: requireEdition,
    },
    {
      path: "/msteams/help",
      name: "msteams_help",
      component: () => import("./msteams/Help.vue"),
      beforeEnter: requireEdition,
      meta: { template: () => import("@/layouts/signin") },
    },
    {
      path: "/profile/:userId",
      component: () => import("./profile/Profile.vue"),
      children: [
        {
          path: "",
          name: "profile",
          component: () => import("./profile/Public.vue"),
          meta: {
            title: "profile",
            template: () => import("@/layouts/standard"),
          },
        },
        {
          path: "favourites",
          name: "profile_favourites",
          component: () => import("./profile/FavouritesNotes.vue"),
          meta: {
            title: "favourites-notes",
            template: () => import("@/layouts/standard"),
          },
        },
        {
          path: "programs",
          name: "profile_stats",
          component: () => import("./profile/ProgramStats.vue"),
          meta: {
            title: "program-stats",
            template: () => import("@/layouts/standard"),
          },
        },
      ],
      beforeEnter: multiguard([requireEdition, requireUser]),
      meta: { template: () => import("@/layouts/standard") },
    },
    {
      path: "/cms/resources",
      name: "cms_resource_list",
      component: () => import("./cms/ResourceList.vue"),
      beforeEnter: multiguard([requireEdition, requireUser]),
      meta: {
        template: () => import("@/layouts/admin"),
        templateProps: {
          showNav: true,
          title: i18n.t("message.resources"),
        }
      },
    },
    {
      path: "/post-program-survey",
      name: "post-program-survey",
      component: () => import("@/survey/PostProgramSurvey"),
      beforeEnter: multiguard([requireEdition]),
      meta: {
        title: "post-program-survey",
        template: () => import("@/survey/SurveyTemplate"),
      },
    },
  ],
});

router.afterEach((to, from) => {
  let editionName = "Nomadic";
  if (store.getters.currentEdition) {
    editionName = store.getters.currentEdition.name;
  }
  let fullTitle = editionName;
  let nearestWithTitle = to.matched
    .slice()
    .reverse()
    .find((r) => r.meta && r.meta.title);
  if (nearestWithTitle) {
    let titleKey = "message." + nearestWithTitle.meta.title;
    let title = i18n.t(titleKey);
    if (title && title !== titleKey) {
      fullTitle = `${title} - ${editionName}`;
    }
  }
  document.title = fullTitle;

  // Close navigation menus
  store.commit('setMenuOpen', null);
});

export default router;
